// no dangerouslySetInnerHTML — titles are user-controlled text (SEC-REQ-4)
//
// V2 source path: customer/midas/components/v2/history/MidasHistoryMachineFiltersV2.tsx
// V1 source: none (greenfield — Phase 4)
// Strategy: scratch
// Verified no-MUI: yes
// Phase: 4 (sub-phase 4.3 — repalette to R5-0 designer JSX)
//
// Horizontal chip row: All machines / Platform / per-machine. Single-select.
// Selection triggers a server-side refetch via the parent's onChange handler.
//
// Chip source = the user's full fleet (sub-phase 4.3 D7 swap; chips populated
// from `state.customerMachinery.machines` in the parent page) so brand-new
// machines are filterable before any chat exists about them. Overflow past
// MAX_VISIBLE_CHIPS collapses into a `+N more` plain mono affordance.
//
// VISUAL PATTERN (per R5-0 designer JSX, replaces the previous Work-Board
// `dt`-palette pattern that was leaking cool charcoal/slate into V2):
//   • Container: flex items-center flex-wrap gap-1.5 (6px gap)
//   • FILTER eyebrow: JBM 10/12 weight 500, ls 0.14em, color #7A7165, mr 6px
//   • Active chip: rounded-[999px] py-[5px] px-[11px] bg-[#1A1614]
//                  → Sora 12/16 weight 500 ls -0.005em color #FAF7F0
//   • Inactive chip: rounded-[999px] py-[5px] px-[11px] bg-white
//                    border 1px #E2DDD3
//                    → Sora 12/16 weight 400 color #3D362F
//   • +N more affordance: same padding, NO bg, NO border
//                         → JBM 11/14 color #7A7165
//
// IMPORTANT: locked decision D2 (2026-05-04) — `Platform` chip is RETAINED.
// Do NOT remove it without explicit user instruction.
import React from "react";

import { HistoryMachineFilter } from "../../../midasHistoryPageSlice";

export interface MachineChip {
  id: string;
  label: string;
}

interface MidasHistoryMachineFiltersV2Props {
  active: HistoryMachineFilter;
  machines: MachineChip[];
  onChange: (next: HistoryMachineFilter) => void;
  /** Translation function. */
  t: (key: string, fallback: string) => string;
}

/**
 * Maximum number of per-machine chips to render before collapsing the rest
 * into a `+N more` affordance. Tuned to match the R5-0 designer artboard
 * (4 named chips visible + +N more) on the 1440 desktop layout.
 */
const MAX_VISIBLE_CHIPS = 5;

/**
 * Fully-rounded pill chip per R5-0 designer JSX. Active = ink fill on warm
 * graphite (#1A1614). Inactive = white surface + 1px warm-stone border. Hover
 * subtly warms the inactive surface; focus shows the standard terracotta ring.
 */
const Chip: React.FC<{
  isActive: boolean;
  onClick: () => void;
  label: string;
  ariaLabel: string;
}> = ({ isActive, onClick, label, ariaLabel }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={isActive}
    aria-label={ariaLabel}
    className={
      isActive
        ? // Active: bg #1A1614, fg #FAF7F0, Sora 12/16 weight 500 ls -0.005em.
          // Padding = py-[5px] px-[11px] (R5-0 py-1.25 px-2.75 fractional spacing
          // not in the project's tailwind config — use bracket-arbitraries).
          "flex-shrink-0 cursor-pointer whitespace-nowrap rounded-[999px] border-0 bg-[#1A1614] px-[11px] py-[5px] text-[12px] font-medium leading-[16px] tracking-[-0.005em] text-[#FAF7F0] outline-none transition-all duration-150 focus-visible:ring-2 focus-visible:ring-midas-terracotta motion-reduce:transition-none"
        : // Inactive: bg white, 1px #E2DDD3 border, fg #3D362F, Sora 12/16
          // weight 400. Hover warms to #FAF7F0 (matches ⌘ K pill bg).
          "flex-shrink-0 cursor-pointer whitespace-nowrap rounded-[999px] border border-solid border-[#E2DDD3] bg-white px-[11px] py-[5px] text-[12px] font-normal leading-[16px] text-[#3D362F] outline-none transition-all duration-150 hover:bg-[#FAF7F0] focus-visible:ring-2 focus-visible:ring-midas-terracotta motion-reduce:transition-none"
    }
    style={{ fontFamily: "'Sora', sans-serif" }}
  >
    {label}
  </button>
);

const MidasHistoryMachineFiltersV2: React.FC<MidasHistoryMachineFiltersV2Props> = ({
  active,
  machines,
  onChange,
  t,
}) => {
  // Slice fleet into visible + overflow per MAX_VISIBLE_CHIPS. If the active
  // filter sits beyond the visible window, swap it into the visible set so
  // the user never loses sight of their current selection.
  let visibleMachines = machines.slice(0, MAX_VISIBLE_CHIPS);
  let overflowCount = Math.max(0, machines.length - MAX_VISIBLE_CHIPS);
  if (
    active !== "all" &&
    active !== "platform" &&
    active &&
    !visibleMachines.some((m) => m.id === active)
  ) {
    const activeMachine = machines.find((m) => m.id === active);
    if (activeMachine) {
      // Replace the last visible chip with the active machine so the active
      // selection is always rendered as a real chip (not hidden in `+N more`).
      visibleMachines = [
        activeMachine,
        ...visibleMachines.slice(0, MAX_VISIBLE_CHIPS - 1),
      ];
      overflowCount = Math.max(0, machines.length - MAX_VISIBLE_CHIPS);
    }
  }

  return (
    <div
      role="tablist"
      aria-label={t("midas.historyPage.filterAriaLabel", "Filter by machine")}
      // R5-0 container: flex items-center flex-wrap gap-1.5 (6px gap).
      className="flex w-full flex-wrap items-center gap-1.5"
    >
      {/* FILTER eyebrow per R5-0: JBM 10/12 weight 500 ls 0.14em #7A7165, mr 6px */}
      <span
        aria-hidden="true"
        className="mr-1.5 inline-block text-[10px] font-medium uppercase leading-3 tracking-[0.14em] text-midas-stone-warm"
        style={{ fontFamily: "'JetBrains Mono', monospace" }}
      >
        {t("midas.historyPage.filterEyebrow", "FILTER")}
      </span>

      <Chip
        isActive={active === "all"}
        onClick={() => onChange("all")}
        label={t("midas.historyPage.filterAll", "All machines")}
        ariaLabel={t("midas.historyPage.filterAllAria", "Show all conversations")}
      />
      <Chip
        isActive={active === "platform"}
        onClick={() => onChange("platform")}
        label={t("midas.historyPage.filterPlatform", "Platform")}
        ariaLabel={t(
          "midas.historyPage.filterPlatformAria",
          "Show platform conversations only"
        )}
      />
      {visibleMachines.map((m) => (
        <Chip
          key={m.id}
          isActive={active === m.id}
          onClick={() => onChange(m.id)}
          label={m.label}
          ariaLabel={`${t("midas.historyPage.filterMachineAria", "Filter by machine")}: ${m.label}`}
        />
      ))}
      {overflowCount > 0 && (
        // +N more per R5-0: same padding as chips, NO bg, NO border, JBM 11/14
        // #7A7165. Spec calls for "plain mono affordance (no chip frame)".
        // Rendered as a non-interactive span — clicking does nothing yet
        // (overflow drawer is a future enhancement; see spec §3D.4).
        <span
          aria-hidden="true"
          className="flex-shrink-0 whitespace-nowrap rounded-[999px] px-[11px] py-[5px] text-[11px] leading-[14px] text-midas-stone-warm"
          style={{ fontFamily: "'JetBrains Mono', monospace" }}
        >
          {`+${overflowCount} ${t("midas.historyPage.filterMore", "more")}`}
        </span>
      )}
    </div>
  );
};

export default MidasHistoryMachineFiltersV2;
